import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Plus, Edit2, Trash2, Search, Tag, X, Save, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import ConfirmationModal from '../components/ConfirmationModal';

// Settings → Knowledge Base. Operators paste in Q&A pairs the AI Reply
// Suggester uses as authoritative source material. The page is a flat
// list — no folders, no nesting — because tenants typically end up with
// dozens of entries, not hundreds, and a search box plus tags is enough.

const KnowledgeBase = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | {} (new) | entry
  const [deleting, setDeleting] = useState(null);

  const { data: entries = [], isLoading } = useQuery(
    ['knowledge', search],
    () => api.get('/knowledge', { params: { q: search || undefined } }).then((r) => r.data),
    { staleTime: 10 * 1000 }
  );

  const saveMutation = useMutation(
    (payload) => {
      if (payload._id) {
        return api.put(`/knowledge/${payload._id}`, payload).then((r) => r.data);
      }
      return api.post('/knowledge', payload).then((r) => r.data);
    },
    {
      onSuccess: () => {
        toast.success('Saved');
        queryClient.invalidateQueries(['knowledge']);
        setEditing(null);
      },
      onError: (e) => toast.error(e?.response?.data?.error || 'Failed to save'),
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/knowledge/${id}`).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Deleted');
        queryClient.invalidateQueries(['knowledge']);
        setDeleting(null);
      },
      onError: (e) => toast.error(e?.response?.data?.error || 'Failed to delete'),
    }
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-gray-900">
            <BookOpen size={20} />
            <h1 className="text-2xl font-bold">Knowledge Base</h1>
          </div>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Hand-curated Q&amp;A pairs the AI Reply Suggester in the Inbox uses as authoritative source material.
            Add the things your team gets asked about most often — pricing, hours, scheduling, policies.
          </p>
        </div>
        <button
          onClick={() => setEditing({})}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-md"
        >
          <Plus size={16} /> Add Q&amp;A
        </button>
      </div>

      <div className="mb-4 relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search question, answer, or tag…"
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400 p-6 text-center">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 px-4 border-2 border-dashed border-gray-200 rounded-lg">
          <BookOpen size={28} className="mx-auto text-gray-300 mb-2" />
          <div className="text-sm text-gray-600">
            {search ? 'No entries match your search.' : 'No knowledge entries yet.'}
          </div>
          {!search && (
            <button
              onClick={() => setEditing({})}
              className="mt-3 text-xs text-primary-600 hover:text-primary-800"
            >
              Add your first Q&amp;A →
            </button>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {entries.map((entry) => (
            <li
              key={entry._id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900">{entry.question}</div>
                  <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap break-words">{entry.answer}</div>
                  {(entry.tags || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {entry.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700"
                        >
                          <Tag size={10} /> {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 text-[11px] text-gray-400">
                    {entry.createdBy?.name || entry.createdBy?.email || 'System'} · updated {new Date(entry.updatedAt).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => setEditing(entry)}
                    className="p-1.5 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100"
                    title="Edit"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => setDeleting(entry)}
                    className="p-1.5 text-red-400 hover:text-red-600 rounded hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <KnowledgeEditor
          entry={editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => saveMutation.mutate(payload)}
          isSaving={saveMutation.isLoading}
        />
      )}

      <ConfirmationModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleteMutation.mutate(deleting._id)}
        title="Delete this entry?"
        message={`"${deleting?.question?.slice(0, 80) || ''}" will be removed from the knowledge base.`}
        confirmText="Delete"
        isDanger
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

const KnowledgeEditor = ({ entry, onClose, onSave, isSaving }) => {
  const isNew = !entry?._id;
  const [question, setQuestion] = useState(entry?.question || '');
  const [answer, setAnswer] = useState(entry?.answer || '');
  const [tagsInput, setTagsInput] = useState((entry?.tags || []).join(', '));

  const tags = useMemo(
    () => tagsInput.split(',').map((t) => t.trim()).filter(Boolean),
    [tagsInput]
  );

  const submit = (e) => {
    e?.preventDefault?.();
    if (!question.trim() || !answer.trim()) {
      toast.error('Question and answer are both required');
      return;
    }
    onSave({ _id: entry?._id, question: question.trim(), answer: answer.trim(), tags });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <form
          onSubmit={submit}
          className="bg-white rounded-lg shadow-xl w-full max-w-2xl pointer-events-auto max-h-[90vh] overflow-y-auto"
        >
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              {isNew ? 'Add Q&A' : 'Edit Q&A'}
            </h2>
            <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700">
              <X size={18} />
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Question</label>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="What does the customer typically ask?"
                rows={2}
                maxLength={500}
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <div className="text-[11px] text-gray-400 mt-0.5">{question.length}/500</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Answer</label>
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="The reply the AI should base its draft on."
                rows={6}
                maxLength={5000}
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <div className="text-[11px] text-gray-400 mt-0.5">{answer.length}/5000</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tags <span className="text-gray-400 font-normal">(optional, comma-separated)</span></label>
              <input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="pricing, scheduling, veneers"
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700">
                      <Tag size={10} /> {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium rounded-md"
            >
              <Save size={14} />
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default KnowledgeBase;
